<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Popover from '@uiKit/elements/Popover.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'
import { formatAed, formatTokenPrice } from '@shared/utils'
import type { OrderFeeLine } from '@modules/orders'
import InvestFeeBreakdown from './InvestFeeBreakdown.vue'
import { ACQUISITION_FEE_CODE, splitSummaryFees } from '../utils/summaryFeeUtils'

// INVESTMENT SUMMARY (Figma 32590:66206): price per token, investment value,
// DLD / Title Deed / NOC / Acquisition fee rows (Acquisition ▾ opens that
// line's rate and VAT), any other charged lines, and the total payable.

interface Props {
  tokenPrice: number
  grossAmount: number
  totalFeeAmount: number
  netAmount: number
  fees: OrderFeeLine[]
  /** A fresh quote is loading — amounts show placeholders. */
  isQuoting: boolean
  /** What the Price Per Token ⓘ opens. */
  tokenPriceSheet: InfoSheetContent
}

const props = defineProps<Props>()

const MISSING_AMOUNT = '-'

const feeRows = computed(() => splitSummaryFees(props.fees, props.totalFeeAmount))
const acquisitionRow = computed(() => feeRows.value.find((row) => row.key === ACQUISITION_FEE_CODE))

const feePopover = ref<InstanceType<typeof Popover> | null>(null)
</script>

<template>
  <Box direction="col" gap="4" class="summary">
    <Text type="body" weight="bold" color="var(--p-surface-400)" is="h3">INVESTMENT SUMMARY</Text>

    <Box direction="col" gap="3">
      <Box align="center" justify="between" gap="3">
        <Box align="center" gap="2">
          <Text type="body" weight="medium" color="var(--p-surface-600)" is="span"
            >Price Per Token</Text
          >
          <InfoSheetButton
            :content="tokenPriceSheet"
            label="About the price per token"
            :size="14"
          />
        </Box>
        <Text type="body" weight="bold" is="span"
          ><CurrencyText :text="formatTokenPrice(tokenPrice)"
        /></Text>
      </Box>

      <Box align="center" justify="between" gap="3">
        <Text type="body" weight="medium" color="var(--p-surface-600)" is="span"
          >Investment Value</Text
        >
        <Skeleton v-if="isQuoting" width="72px" height="16px" border-radius="4px" />
        <Text v-else type="body" weight="bold" is="span"
          ><CurrencyText :text="formatAed(grossAmount)"
        /></Text>
      </Box>

      <Box v-for="row in feeRows" :key="row.key" align="center" justify="between" gap="3">
        <Box
          v-if="row.key === ACQUISITION_FEE_CODE"
          is="button"
          type="button"
          align="center"
          gap="2"
          class="summary__fee-toggle"
          :disabled="!row.line"
          aria-haspopup="dialog"
          @click="feePopover?.toggle($event)"
        >
          <Text type="body" weight="medium" color="var(--p-surface-600)" is="span">{{
            row.label
          }}</Text>
          <PiIcon v-if="row.line" name="chevron-down" class="summary__chevron" />
        </Box>
        <Text v-else type="body" weight="medium" color="var(--p-surface-600)" is="span">{{
          row.label
        }}</Text>
        <Skeleton v-if="isQuoting" width="72px" height="16px" border-radius="4px" />
        <Text v-else type="body" weight="bold" is="span"
          ><CurrencyText :text="row.amount === null ? MISSING_AMOUNT : formatAed(row.amount)"
        /></Text>
      </Box>
    </Box>

    <Box class="summary__rule" />

    <Box align="center" justify="between" gap="3">
      <Text type="body" weight="bold" is="span">TOTAL PAYABLE</Text>
      <Skeleton v-if="isQuoting" width="110px" height="24px" border-radius="6px" />
      <Text v-else type="page-title" weight="bold" is="span"
        ><CurrencyText :text="formatAed(netAmount)"
      /></Text>
    </Box>

    <Popover ref="feePopover">
      <Box class="summary__popover">
        <InvestFeeBreakdown
          v-if="acquisitionRow?.line"
          :fees="[acquisitionRow.line]"
          :total-fee-amount="acquisitionRow.amount ?? 0"
          :title="acquisitionRow.label"
        />
      </Box>
    </Popover>
  </Box>
</template>

<style scoped>
.summary {
  padding: 20px;
  border: 1px solid var(--p-surface-200);
  border-radius: 20px;
  background: var(--p-surface-0);
  filter: drop-shadow(0 8px 15px rgba(0, 0, 0, 0.04));
}

.summary__fee-toggle {
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.summary__fee-toggle:disabled {
  cursor: default;
}

.summary__fee-toggle:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
  border-radius: 4px;
}

.summary__chevron {
  font-size: 12px;
  color: var(--p-primary-600);
}

.summary__rule {
  height: 1px;
  background: var(--p-surface-100);
}

.summary__popover {
  width: min(471px, calc(100vw - 32px));
  max-height: min(751px, 70vh);
  overflow-y: auto;
  padding: 14px 24px 30px;
}
</style>
